import React, { useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Plus, MoreHorizontal, Pause, Play, X, Calendar, CalendarOff, CalendarCheck } from "lucide-react"
import { formatTimestamp, parseApiTimestamp } from "@/lib/datetime"
import { PostponeDialog } from "@/components/campaigns/PostponeDialog"

interface Campaign {
  id: string
  definitionId: string
  name: string
  status: "active" | "paused" | "completed" | "removed"
  nextCallDate?: string | null
  attempts: number
  maxAttempts: number
  enrolledAt: string
  pauseStart?: string | null
  pauseEnd?: string | null
  pauseReason?: string | null
}

interface PatientCampaignsProps {
  patientId: string
  campaigns?: Campaign[]
  campaignHistory?: Campaign[]
  loading?: boolean
  onAddToCampaign?: () => void
  onPauseCampaign?: (definitionId: string, enrollmentId: string) => void
  onResumeCampaign?: (definitionId: string, enrollmentId: string) => void
  onRemoveFromCampaign?: (definitionId: string, enrollmentId: string) => void
  onReschedule?: (campaignId: string) => void
  onPostponeEnrollment?: (definitionId: string, enrollmentId: string, pauseStart: string, pauseEnd: string, reason?: string) => Promise<void>
  onClearEnrollmentPostpone?: (definitionId: string, enrollmentId: string) => Promise<void>
}

export function PatientCampaigns({
  patientId,
  campaigns = [],
  campaignHistory = [],
  loading = false,
  onAddToCampaign,
  onPauseCampaign,
  onResumeCampaign,
  onRemoveFromCampaign,
  onReschedule,
  onPostponeEnrollment,
  onClearEnrollmentPostpone,
}: PatientCampaignsProps) {
  const [postponeDialogOpen, setPostponeDialogOpen] = useState(false)
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null)

  const isPostponed = (campaign: Campaign) => {
    return campaign.pauseStart && campaign.pauseEnd
  }

  const handlePostpone = (campaign: Campaign) => {
    setSelectedCampaign(campaign)
    setPostponeDialogOpen(true)
  }

  const handlePostponeConfirm = async (pauseStart: string, pauseEnd: string, reason?: string) => {
    if (!selectedCampaign || !onPostponeEnrollment) return
    await onPostponeEnrollment(
      selectedCampaign.definitionId,
      selectedCampaign.id,
      pauseStart,
      pauseEnd,
      reason
    )
  }

  const handleClearPostpone = async (campaign: Campaign) => {
    if (!onClearEnrollmentPostpone) return
    await onClearEnrollmentPostpone(campaign.definitionId, campaign.id)
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-40" />
        <Skeleton className="h-64" />
        <Skeleton className="h-48" />
      </div>
    )
  }

  const activeCampaigns = campaigns.filter(
    (c) => c.status === "active" || c.status === "paused"
  )
  const completedCampaigns = [
    ...campaigns.filter((c) => c.status === "completed" || c.status === "removed"),
    ...campaignHistory,
  ]

  const getStatusBadge = (status: Campaign["status"]) => {
    switch (status) {
      case "active":
        return (
          <Badge className="bg-success/10 text-success hover:bg-success/10">
            Active
          </Badge>
        )
      case "paused":
        return (
          <Badge className="bg-warning/10 text-warning hover:bg-warning/10">
            Paused
          </Badge>
        )
      case "completed":
        return (
          <Badge className="bg-info/10 text-info hover:bg-info/10">
            Completed
          </Badge>
        )
      case "removed":
        return (
          <Badge variant="outline" className="text-muted-foreground">
            Removed
          </Badge>
        )
      default:
        return <Badge variant="outline">{status}</Badge>
    }
  }

  return (
    <div className="space-y-6">
      {/* Actions */}
      <div className="flex justify-end">
        <Button onClick={onAddToCampaign} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add to Campaign
        </Button>
      </div>

      {/* Active Campaigns */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Active Campaigns</CardTitle>
        </CardHeader>
        <CardContent>
          {activeCampaigns.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              <p>Not enrolled in any active campaigns</p>
              <Button
                variant="outline"
                onClick={onAddToCampaign}
                className="mt-4"
              >
                Add to Campaign
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[160px]">Campaign</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="whitespace-nowrap">Next Call</TableHead>
                  <TableHead>Attempts</TableHead>
                  <TableHead className="w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeCampaigns.map((campaign) => (
                  <TableRow key={campaign.id}>
                    <TableCell className="font-medium">
                      {campaign.name}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {getStatusBadge(campaign.status)}
                        {isPostponed(campaign) && (
                          <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30 text-xs w-fit">
                            <CalendarOff className="h-3 w-3 mr-1" />
                            Paused {formatTimestamp(campaign.pauseStart, "MMM d")} - {formatTimestamp(campaign.pauseEnd, "MMM d")}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {campaign.nextCallDate ? (
                        parseApiTimestamp(campaign.nextCallDate)?.toLocaleString("en-US", {
                          month: "short",
                          day: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                          timeZoneName: "short",
                        })
                      ) : campaign.status === "paused" ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <span className="text-muted-foreground">Not scheduled</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {campaign.attempts}/{campaign.maxAttempts}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {campaign.status === "active" ? (
                            <DropdownMenuItem
                              onClick={() => onPauseCampaign?.(campaign.definitionId, campaign.id)}
                            >
                              <Pause className="h-4 w-4 mr-2" />
                              Pause
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={() => onResumeCampaign?.(campaign.definitionId, campaign.id)}
                            >
                              <Play className="h-4 w-4 mr-2" />
                              Resume
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => onReschedule?.(campaign.id)}
                          >
                            <Calendar className="h-4 w-4 mr-2" />
                            Reschedule
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {isPostponed(campaign) ? (
                            <DropdownMenuItem
                              onClick={() => handleClearPostpone(campaign)}
                            >
                              <CalendarCheck className="h-4 w-4 mr-2" />
                              Clear Pause Dates
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={() => handlePostpone(campaign)}
                            >
                              <CalendarOff className="h-4 w-4 mr-2" />
                              Pause Calls
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => onRemoveFromCampaign?.(campaign.definitionId, campaign.id)}
                            className="text-destructive"
                          >
                            <X className="h-4 w-4 mr-2" />
                            Remove
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Campaign History */}
      {completedCampaigns.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Campaign History</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[160px]">Campaign</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Enrolled</TableHead>
                  <TableHead>Attempts</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {completedCampaigns.map((campaign) => (
                  <TableRow key={campaign.id}>
                    <TableCell className="font-medium">
                      {campaign.name}
                    </TableCell>
                    <TableCell>{getStatusBadge(campaign.status)}</TableCell>
                    <TableCell>
                      {formatTimestamp(campaign.enrolledAt, "MMM d, yyyy")}
                    </TableCell>
                    <TableCell>
                      {campaign.attempts}/{campaign.maxAttempts}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Pause Dialog */}
      <PostponeDialog
        open={postponeDialogOpen}
        onOpenChange={setPostponeDialogOpen}
        title="Pause Enrollment"
        description="Set a date range during which calls for this patient will be paused. Calls will automatically resume after the pause ends."
        onConfirm={handlePostponeConfirm}
        currentPauseStart={selectedCampaign?.pauseStart}
        currentPauseEnd={selectedCampaign?.pauseEnd}
      />
    </div>
  )
}

export default PatientCampaigns
